import { FC, useMemo, useState } from 'react';
import { Grid, Typography, Box, CircularProgress, Alert, Chip, Card, CardContent, Divider, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useGuardianState } from '../../../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGuardianCoursesClassesHook } from '../../../../hooks/useGuardianAcademicHook';
import PaginatedGrid from '../../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ICourseResponse, IClassResponse } from '../../../../../../../apis/academics/academicsClassesAndCourses.interfaces';

enum EViewType {
  GRID = 'grid',
  DETAILS = 'details',
}

const GuardianCoursesClassesView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCourse, setSelectedCourse] = useState<ICourseResponse | null>(null);
  const [viewType, setViewType] = useState<EViewType>(EViewType.GRID);

  const { data, isLoading, error } = useGuardianCoursesClassesHook(
    selectedChildId || '',
    {
      page,
      limit: pageSize,
    }
  );

  const classes: IClassResponse[] = useMemo(() => data?.classes?.classes || [], [data?.classes?.classes]);
  const courses: ICourseResponse[] = useMemo(() => data?.courses?.courses || [], [data?.courses?.courses]);
  const totalCourses = useMemo(() => data?.courses?.total || 0, [data?.courses?.total]);
  const classInfo = useMemo(() => classes[0], [classes]); // Get child's class info

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'name',
      headerName: translateText('guardian.academics.coursesClasses.columns.courseName'),
      width: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'subjectCode',
      headerName: translateText('guardian.academics.coursesClasses.columns.subjectCode'),
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color="default"
          variant="outlined"
        />
      ),
    },
    {
      field: 'teacher',
      headerName: translateText('guardian.academics.coursesClasses.columns.teacher'),
      width: 180,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const course = params.row as ICourseResponse;
        return (
          <Typography variant="body2">
            {course.teacher?.name || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'subjectType',
      headerName: translateText('guardian.academics.coursesClasses.columns.subjectType'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const subjectType = params.value as string;
        const getChipColor = (type?: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
          if (!type) return 'default';
          const upperType = type.toUpperCase();
          if (upperType.includes('SCIENCE')) return 'info';
          if (upperType.includes('MATH')) return 'secondary';
          if (upperType.includes('LANGUAGE')) return 'success';
          if (upperType.includes('COMPUTER')) return 'warning';
          if (upperType.includes('ARTS')) return 'error';
          return 'default';
        };
        return (
          <Chip
            label={subjectType}
            size="small"
            color={getChipColor(subjectType)}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'totalLectures',
      headerName: translateText('guardian.academics.coursesClasses.columns.totalLectures'),
      width: 130,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      ),
    },
  ], [translateText]);

  const rows = useMemo(() => {
    return courses.map((course: ICourseResponse) => ({
      ...course, // Include full course object for detail view
    }));
  }, [courses]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  const handleRowClick = (params: any) => {
    const course = courses.find((c: ICourseResponse) => c.id === params.row.id);
    if (course) {
      setSelectedCourse(course);
      setViewType(EViewType.DETAILS);
    }
  };

  const handleBackToGrid = () => {
    setViewType(EViewType.GRID);
    setSelectedCourse(null);
  };

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-courses-classes-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-courses-classes-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-courses-classes-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('common.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (viewType === EViewType.DETAILS && selectedCourse) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-courses-classes-container">
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToGrid}
              data-testid="back-to-grid-button"
            >
              {translateText('common.back')}
            </Button>
            <Typography variant="h6" data-testid="course-detail-title">
              {selectedCourse.name}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Card data-testid="course-detail-card">
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translateText('guardian.academics.coursesClasses.details.courseName')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedCourse.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translateText('guardian.academics.coursesClasses.columns.subjectCode')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedCourse.subjectCode}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translateText('guardian.academics.coursesClasses.columns.teacher')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedCourse.teacher?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translateText('guardian.academics.coursesClasses.columns.subjectType')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedCourse.subjectType}
                  </Typography>
                </Grid>
                {selectedCourse.totalLectures && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {translateText('guardian.academics.coursesClasses.columns.totalLectures')}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedCourse.totalLectures}
                    </Typography>
                  </Grid>
                )}
                {selectedCourse.description && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {translateText('guardian.academics.coursesClasses.details.description')}
                    </Typography>
                    <Typography variant="body2">
                      {selectedCourse.description}
                    </Typography>
                  </Grid>
                )}
                {selectedCourse.syllabus && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {translateText('guardian.academics.coursesClasses.details.syllabus')}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedCourse.syllabus}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-courses-classes-container">
      {classInfo && (
        <Grid item xs={12}>
          <Card sx={{ mb: 2 }} data-testid="class-info-card">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {translateText('guardian.academics.coursesClasses.classInfo')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {translateText('guardian.academics.coursesClasses.columns.className')}
                  </Typography>
                  <Typography variant="body1">
                    {classInfo.name || '-'}
                  </Typography>
                </Grid>
                {classInfo.section && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {translateText('guardian.academics.coursesClasses.columns.section')}
                    </Typography>
                    <Typography variant="body1">
                      {classInfo.section}
                    </Typography>
                  </Grid>
                )}
                {classInfo.standardLevel && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {translateText('guardian.academics.coursesClasses.columns.standard')}
                    </Typography>
                    <Typography variant="body1">
                      {typeof classInfo.standardLevel === 'object'
                        ? classInfo.standardLevel.name
                        : classInfo.standardLevel}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2 }} data-testid="courses-title">
          {translateText('guardian.academics.coursesClasses.title')}
        </Typography>
      </Grid>
      {courses.length === 0 ? (
        <Grid item xs={12}>
          <Alert severity="info">
            {translateText('guardian.academics.coursesClasses.noCourses')}
          </Alert>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <PaginatedGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            dataTestId="guardian-courses-classes-grid"
            serverSide={true}
            totalCount={totalCourses}
            currentPage={page}
            currentPageSize={pageSize}
            onPageChange={handlePageChange}
            onRowClick={handleRowClick}
            rowsPerPageOptions={[5, 10, 25, 50]}
            hideColumnsOnMobile={['subjectType', 'totalLectures']}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default GuardianCoursesClassesView;
